import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Chip } from 'primereact/chip';
import { Message } from 'primereact/message';
import { Finding } from '@/api/domains/analyses/types';
import {
  useCreateValidation,
  useLatestValidation
} from '@/api/domains/validation';
import {
  ValidationStatus,
  ValidationReason
} from '@/api/domains/validation/types';
import styles from './ValidationPanel.module.css';
import { ANALYSIS_ENABLED } from '@/config/features';
import { VALIDATION_RULES } from '@/api/domains/validation/utils/validationMatrix';
import {
  VALIDATION_REASON_LABELS,
  VALIDATION_STATUS_LABELS
} from '@/api/domains/validation/utils/validationLabels';
import { formatDate } from '@/utils/formatDate';

interface ValidationPanelProps {
  finding: Finding;
}

interface StatusOption {
  status: ValidationStatus;
  label: string;
  icon: string;
  description: string;
}

const STATUS_OPTIONS: StatusOption[] = [
  {
    status: 'confirmed',
    label: 'Confirmed',
    icon: 'pi-check-circle',
    description: 'Valid finding'
  },
  {
    status: 'false_positive',
    label: 'False Positive',
    icon: 'pi-times-circle',
    description: 'Incorrectly flagged'
  },
  {
    status: 'partially_confirmed',
    label: 'Partially Confirmed',
    icon: 'pi-info-circle',
    description: 'Needs refinement'
  },
  {
    status: 'not_applicable',
    label: 'Not Applicable',
    icon: 'pi-minus-circle',
    description: 'Out of scope'
  },
  {
    status: 'needs_clarification',
    label: 'Needs Clarification',
    icon: 'pi-question-circle',
    description: 'Requires more info'
  },
  {
    status: 'duplicate',
    label: 'Duplicate',
    icon: 'pi-copy',
    description: 'Already reported'
  },
  {
    status: 'accepted_risk',
    label: 'Accepted Risk',
    icon: 'pi-lock',
    description: 'Acknowledged risk'
  }
];

export const ValidationPanel = ({ finding }: ValidationPanelProps) => {
  const [status, setStatus] = useState<ValidationStatus | null>(null);
  const [reason, setReason] = useState<ValidationReason | null>(null);
  const [comment, setComment] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const currentFindingId = useRef<string>(finding.finding_id);

  const {
    data: latestValidation,
    isLoading: isLoadingValidation,
    error: validationError
  } = useLatestValidation(finding.finding_id);

  const createValidation = useCreateValidation();

  const analysisEnabled = ANALYSIS_ENABLED;

  const resetStates = () => {
    if (latestValidation) {
      setStatus(latestValidation.status as ValidationStatus);
      setReason(
        latestValidation.reason
          ? (latestValidation.reason as ValidationReason)
          : null
      );
      setComment(latestValidation.comment || '');
      setHasUnsavedChanges(false);
    } else {
      setStatus(null);
      setReason(null);
      setComment('');
      setHasUnsavedChanges(false);
    }
  };

  useEffect(() => {
    resetStates();
  }, [
    latestValidation?.validation_id,
    latestValidation?.status,
    latestValidation?.reason,
    latestValidation?.comment,
    latestValidation?.validated_at
  ]);

  useEffect(() => {
    if (currentFindingId.current !== finding.finding_id) {
      currentFindingId.current = finding.finding_id;
      setStatus(null);
      setReason(null);
      setComment('');
      setHasUnsavedChanges(false);
    }
  }, [finding.finding_id]);

  const handleStatusChange = (newStatus: ValidationStatus) => {
    setStatus(newStatus);
    setHasUnsavedChanges(true);

    const rules = VALIDATION_RULES[newStatus];
    if (!rules.requiresReason) {
      setReason(null);
    }
  };

  const handleReasonChange = (newReason: ValidationReason) => {
    setReason(newReason);
    setHasUnsavedChanges(true);
  };

  const handleCommentChange = (newComment: string) => {
    setComment(newComment);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!status) return;

    const rules = VALIDATION_RULES[status];
    if (rules.requiresReason && !reason) return;
    if (rules.requiresComment && !comment.trim()) return;

    try {
      const saved = await createValidation.mutateAsync({
        payload: {
          finding_id: finding.finding_id,
          status,
          reason: reason || null,
          comment: comment.trim() || null
        },
        options: {
          enforce_reason: true,
          latest_wins: true
        }
      });

      if (saved) {
        setStatus(saved.status as ValidationStatus);
        setReason(saved.reason ? (saved.reason as ValidationReason) : null);
        setComment(saved.comment || '');
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Failed to save validation:', error);
    }
  };

  const handleResetUnsavedChanges = () => {
    resetStates();
  };

  const currentRules = status ? VALIDATION_RULES[status] : null;

  const isSaveDisabled =
    !status ||
    (currentRules?.requiresReason && !reason) ||
    (currentRules?.requiresComment && !comment.trim()) ||
    createValidation.isPending ||
    !hasUnsavedChanges;

  const reasonOptions =
    currentRules?.typicalReasons.map((r) => ({
      label: VALIDATION_REASON_LABELS[r],
      value: r
    })) || [];

  const displayStatusLabel =
    status ?? (latestValidation?.status as ValidationStatus) ?? null;
  const displayLabel = displayStatusLabel
    ? VALIDATION_STATUS_LABELS[displayStatusLabel]
    : 'No validation yet';
  const showAuthorDate = Boolean(
    latestValidation?.author && latestValidation?.validated_at
  );

  return (
    <div className={styles.validationCard}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <i className="pi pi-verified" style={{ fontSize: '1.2rem' }} />
          <h3 className={styles.title}>Officer Validation</h3>
        </div>
        <div className={styles.headerRight}>
          {hasUnsavedChanges && !createValidation.isPending && (
            <Chip
              label="Unsaved Changes"
              icon="pi pi-exclamation-triangle"
              className={styles.unsavedChip}
            />
          )}
          {latestValidation &&
            !hasUnsavedChanges &&
            !createValidation.isPending && (
              <Chip
                label="Validated"
                icon="pi pi-check"
                className={styles.savedChip}
              />
            )}

          {!latestValidation &&
            !hasUnsavedChanges &&
            !isLoadingValidation &&
            !createValidation.isPending && (
              <Chip
                label="Validation Required"
                icon="pi pi-exclamation-circle"
                className={styles.requiredChip}
              />
            )}

          {(isLoadingValidation || createValidation.isPending) && (
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }} />
          )}
        </div>
      </div>

      {!analysisEnabled && (
        <div className={styles.disabledBanner}>
          <strong>Note:</strong> validations are disabled in this deployment;
          the panel is for demonstration only and the controls are inactive.
        </div>
      )}

      <div className={styles.divider} />

      {createValidation.isError && (
        <Message
          severity="error"
          text={createValidation.error?.message || 'Failed to save validation'}
          className={styles.errorMessage}
        />
      )}

      {validationError && (
        <Message
          severity="warn"
          text="Could not load existing validation"
          className={styles.errorMessage}
        />
      )}

      <div className={styles.statusSection}>
        <label className={styles.sectionLabel}>
          <i className="pi pi-flag" />
          Validation Status
          <span className={styles.required}>*</span>
        </label>
        <div className={styles.statusButtons}>
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.status}
              onClick={() => handleStatusChange(option.status)}
              disabled={createValidation.isPending || !analysisEnabled}
              className={`${styles.statusButton} ${
                status === option.status ? styles.statusButtonActive : ''
              }`}
              title={option.description}
              type="button"
            >
              <i className={`pi ${option.icon}`} />
              <span className={styles.statusButtonLabel}>{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {currentRules?.requiresReason && (
        <div className={styles.reasonSection}>
          <label htmlFor="validation-reason" className={styles.sectionLabel}>
            <i className="pi pi-tag" />
            Reason
            <span className={styles.required}>*</span>
          </label>
          <Dropdown
            id="validation-reason"
            value={reason}
            onChange={(e) => handleReasonChange(e.value)}
            options={reasonOptions}
            placeholder="Select a reason..."
            className={styles.reasonDropdown}
            disabled={createValidation.isPending || !analysisEnabled}
          />
          {currentRules.typicalReasons.length > 0 && (
            <small className={styles.hint}>
              <i className="pi pi-info-circle" />
              Typical reasons for {VALIDATION_STATUS_LABELS[status!]}
            </small>
          )}
        </div>
      )}

      <div className={styles.commentSection}>
        <label htmlFor="validation-comment" className={styles.sectionLabel}>
          <i className="pi pi-comment" />
          Comment
          {currentRules?.requiresComment ? (
            <span className={styles.required}>*</span>
          ) : (
            <span className={styles.optional}>(Optional)</span>
          )}
        </label>
        <InputTextarea
          id="validation-comment"
          value={comment}
          onChange={(e) => handleCommentChange(e.target.value)}
          rows={5}
          placeholder={
            currentRules?.requiresComment
              ? 'Required: Provide detailed reasoning for your validation decision...'
              : 'Add notes, reasoning, or follow-up actions (optional)...'
          }
          className={styles.commentTextarea}
          autoResize={false}
          disabled={createValidation.isPending || !analysisEnabled}
        />
        {status === 'false_positive' && !currentRules?.requiresComment && (
          <small className={styles.hint}>
            <i className="pi pi-lightbulb" />
            Recommendation: Add context for false positive classifications to
            improve AI model accuracy
          </small>
        )}
        <div className={styles.characterCount}>{comment.length} characters</div>
      </div>

      <div className={styles.actionButtons}>
        {hasUnsavedChanges ? (
          <Button
            label="Reset Changes"
            icon="pi pi-eraser"
            onClick={handleResetUnsavedChanges}
            className={styles.resetButton}
            disabled={!analysisEnabled}
          />
        ) : (
          ''
        )}
        <Button
          label={
            createValidation.isPending
              ? 'Saving...'
              : latestValidation && hasUnsavedChanges
                ? 'Update Validation'
                : latestValidation && !hasUnsavedChanges
                  ? 'Validation Saved'
                  : 'Save Validation'
          }
          icon={
            createValidation.isPending
              ? 'pi pi-spin pi-spinner'
              : latestValidation && hasUnsavedChanges
                ? 'pi pi-refresh'
                : latestValidation && !hasUnsavedChanges
                  ? 'pi pi-check'
                  : 'pi pi-save'
          }
          onClick={handleSave}
          disabled={isSaveDisabled || !analysisEnabled}
          className={styles.saveButton}
        />
      </div>

      <div className={styles.statusIndicator}>
        <i className="pi pi-info-circle" />
        <div className={styles.statusIndicatorContent}>
          <span>
            {displayStatusLabel ? (
              <>
                Validated as <strong>{displayLabel}</strong>
              </>
            ) : (
              <>{displayLabel}</>
            )}
          </span>
          {showAuthorDate && (
            <span className={styles.metadata}>
              By {latestValidation!.author} •{' '}
              {formatDate(latestValidation!.validated_at)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
